import { formatDuration } from './activity-format.js';

export interface SleepSpan {
    startAt: string;
    endAt: string | null;
}

export interface SleepBlock {
    /** Fraction of the family day, from 0 at midnight to 1 at the next midnight. */
    start: number;
    end: number;
    running: boolean;
}

export interface SleepRhythm {
    label: string;
    description: string;
    blocks: SleepBlock[];
    /** Fraction of the day for the current instant, when it falls inside the day. */
    now: number | null;
}

interface Interval {
    start: number;
    end: number | null;
}

/**
 * Asleep blocks for one family day, plus the live stretch.
 * Sessions that cross midnight are clipped. Touching sessions draw as one block.
 * With no overlapping sleep, the label does not claim the child has been awake since midnight.
 */
export function sleepRhythm(
    sleeps: readonly SleepSpan[],
    dayFrom: number,
    dayTo: number,
    now: number,
): SleepRhythm {
    const dayMs = dayTo - dayFrom;
    if (!Number.isFinite(dayMs) || dayMs <= 0) {
        return {
            label: 'No sleep logged yet',
            description: 'No sleep logged yet.',
            blocks: [],
            now: null,
        };
    }
    const live = now >= dayFrom && now < dayTo;
    const at = live ? now : dayTo;
    const merged = mergeIntervals(parseIntervals(sleeps));
    const blocks = merged.flatMap((interval) =>
        clipInterval(interval, dayFrom, dayTo, dayMs, now, live),
    );
    const stretch = currentStretch(merged, dayFrom, dayTo, at);
    const label = stretchLabel(stretch);
    return {
        label,
        description: describe(label, blocks.length, stretch.kind),
        blocks,
        now: live ? (now - dayFrom) / dayMs : null,
    };
}

function parseIntervals(sleeps: readonly SleepSpan[]): Interval[] {
    const intervals: Interval[] = [];
    for (const sleep of sleeps) {
        const start = Date.parse(sleep.startAt);
        if (!Number.isFinite(start)) {
            continue;
        }
        if (sleep.endAt === null) {
            intervals.push({ start, end: null });
            continue;
        }
        const end = Date.parse(sleep.endAt);
        if (!Number.isFinite(end) || end <= start) {
            continue;
        }
        intervals.push({ start, end });
    }
    intervals.sort((a, b) => a.start - b.start);
    return intervals;
}

function mergeIntervals(intervals: readonly Interval[]): Interval[] {
    const merged: Interval[] = [];
    for (const interval of intervals) {
        const last = merged[merged.length - 1];
        if (last === undefined) {
            merged.push({ start: interval.start, end: interval.end });
            continue;
        }
        const lastEnd = last.end ?? Number.POSITIVE_INFINITY;
        if (interval.start > lastEnd) {
            merged.push({ start: interval.start, end: interval.end });
            continue;
        }
        if (interval.end === null || last.end === null) {
            last.end = null;
        } else {
            last.end = Math.max(last.end, interval.end);
        }
    }
    return merged;
}

function clipInterval(
    interval: Interval,
    dayFrom: number,
    dayTo: number,
    dayMs: number,
    now: number,
    live: boolean,
): SleepBlock[] {
    if (interval.end === null && interval.start > now) {
        return [];
    }
    const visibleEnd = interval.end ?? Math.min(Math.max(now, interval.start), dayTo);
    const start = Math.max(interval.start, dayFrom);
    const end = Math.min(visibleEnd, dayTo);
    if (end <= start) {
        return [];
    }
    return [
        {
            start: (start - dayFrom) / dayMs,
            end: (end - dayFrom) / dayMs,
            running: interval.end === null && live && interval.start <= now,
        },
    ];
}

type Stretch =
    { kind: 'none' } | { kind: 'asleep'; seconds: number } | { kind: 'awake'; seconds: number };

function currentStretch(
    intervals: readonly Interval[],
    dayFrom: number,
    dayTo: number,
    at: number,
): Stretch {
    const overlapping = intervals.filter((interval) => overlapsDay(interval, dayFrom, dayTo));
    if (overlapping.length === 0) {
        return { kind: 'none' };
    }
    const asleep = overlapping.find((interval) => covers(interval, at, dayTo));
    if (asleep !== undefined) {
        return { kind: 'asleep', seconds: elapsed(asleep.start, at) };
    }
    let anchor: number | undefined;
    for (const interval of overlapping) {
        if (interval.end !== null && interval.end <= at && interval.end > dayFrom) {
            anchor = anchor === undefined ? interval.end : Math.max(anchor, interval.end);
        }
    }
    if (anchor === undefined) {
        return { kind: 'none' };
    }
    return { kind: 'awake', seconds: elapsed(anchor, at) };
}

function overlapsDay(interval: Interval, dayFrom: number, dayTo: number): boolean {
    const end = interval.end ?? Number.POSITIVE_INFINITY;
    return interval.start < dayTo && end > dayFrom;
}

/** True when this sleep still covers `at`. A sleep that ends on the day boundary counts through the end of that day. */
function covers(interval: Interval, at: number, dayTo: number): boolean {
    if (interval.start > at || interval.start >= dayTo) {
        return false;
    }
    if (interval.end === null) {
        return true;
    }
    if (at >= dayTo) {
        return interval.end >= dayTo;
    }
    return interval.end > at;
}

function elapsed(from: number, to: number): number {
    return Math.max(0, Math.floor((to - from) / 1000));
}

function stretchLabel(stretch: Stretch): string {
    if (stretch.kind === 'asleep') {
        return `Asleep for ${formatDuration(stretch.seconds)}`;
    }
    if (stretch.kind === 'awake') {
        return `Awake for ${formatDuration(stretch.seconds)}`;
    }
    return 'No sleep logged yet';
}

function describe(label: string, blocks: number, kind: Stretch['kind']): string {
    if (kind === 'none' || blocks === 0) {
        return 'No sleep logged yet.';
    }
    const noun = blocks === 1 ? '1 sleep block' : `${blocks} sleep blocks`;
    return `${label}. ${noun} today.`;
}
